/**
 * Form-page kit: section cards with save bars, toggles, native selects,
 * textareas, setup tabs, and the consequence-first confirm dialog (three
 * grades of destructiveness per the frozen rule).
 */

import { useEffect, useId, useRef } from 'react';
import { useIntl } from 'react-intl';
import k from './ui.module.css';
import f from './formkit.module.css';
import { Button } from './kit';

export function FormCard(props: {
  title?: string;
  sub?: string;
  children: React.ReactNode;
  onSubmit?: () => void;
  cta?: string;
  ctaDisabled?: boolean;
  ctaDisabledReason?: string;
  busy?: boolean;
  secondary?: { label: string; onClick: () => void };
  footNote?: string;
}) {
  return (
    <form
      className={f.card}
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit?.();
      }}
    >
      {(props.title || props.sub) && (
        <div className={f.cardHead}>
          {props.title && <h2 className={f.cardTitle}>{props.title}</h2>}
          {props.sub && <p className={f.cardSub}>{props.sub}</p>}
        </div>
      )}
      <div className={f.cardBody}>{props.children}</div>
      {(props.cta || props.footNote) && (
        <div className={f.saveBar}>
          {props.cta && (
            <Button
              type="submit"
              variant="brand"
              disabled={props.ctaDisabled || props.busy}
              busy={props.busy}
            >
              {props.cta}
            </Button>
          )}
          {props.secondary && (
            <Button type="button" variant="quiet" onClick={props.secondary.onClick}>
              {props.secondary.label}
            </Button>
          )}
          {props.ctaDisabled && props.ctaDisabledReason && (
            <span className={f.reason}>{props.ctaDisabledReason}</span>
          )}
          {props.footNote && <span className={f.footNote}>{props.footNote}</span>}
        </div>
      )}
    </form>
  );
}

export function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className={f.grid}>{children}</div>;
}

export function Span2({ children }: { children: React.ReactNode }) {
  return <div className={f.span2}>{children}</div>;
}

export function Toggle(props: {
  label: string;
  help?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  stateWord: string;
}) {
  const id = useId();
  return (
    <div className={f.toggleRow}>
      <div>
        <label htmlFor={id} className={f.toggleLabel}>
          {props.label}
        </label>
        {props.help && <div className={f.toggleHelp}>{props.help}</div>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={props.checked}
        className={[f.track, props.checked ? f.trackOn : ''].join(' ')}
        onClick={() => props.onChange(!props.checked)}
      >
        <span className={f.knob} />
        <span className={f.srOnly}>{props.stateWord}</span>
      </button>
    </div>
  );
}

export function SelectField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
  help?: string;
  requirement?: { word: string; kind: 'required' | 'optional' };
  /** ZOY-335: names a problem with the field itself — e.g. options that could not be loaded. */
  error?: string;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const hasError = Boolean(props.error);
  return (
    <label className={k.field} htmlFor={id}>
      <span className={k.fieldTop}>
        <span className={k.fieldLabel}>{props.label}</span>
        {props.requirement && (
          <span
            className={[
              k.fieldReq,
              props.requirement.kind === 'required' ? k.fieldReqRequired : k.fieldReqOptional,
            ].join(' ')}
          >
            {props.requirement.word}
          </span>
        )}
      </span>
      <span className={[k.fieldBox, hasError ? k.fieldBoxError : ''].join(' ')}>
        <select
          id={id}
          className={k.fieldInput}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          aria-invalid={hasError || undefined}
          aria-describedby={helpId}
        >
          {props.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </span>
      <span id={helpId} className={[k.fieldHelp, hasError ? k.fieldHelpError : ''].join(' ')}>
        {props.error ?? props.help ?? ' '}
      </span>
    </label>
  );
}

export function TextAreaField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
  rows?: number;
  requirement?: { word: string; kind: 'required' | 'optional' };
  /** Sealed content shown in the same frame — not typeable, and announced as such. */
  readOnly?: boolean;
}) {
  const id = useId();
  return (
    <label className={k.field} htmlFor={id}>
      <span className={k.fieldTop}>
        <span className={k.fieldLabel}>{props.label}</span>
        {props.requirement && (
          <span
            className={[
              k.fieldReq,
              props.requirement.kind === 'required' ? k.fieldReqRequired : k.fieldReqOptional,
            ].join(' ')}
          >
            {props.requirement.word}
          </span>
        )}
      </span>
      <span className={[k.fieldBox, f.areaBox, props.readOnly ? k.fieldBoxReadOnly : ''].join(' ')}>
        <textarea
          id={id}
          className={[k.fieldInput, f.area].join(' ')}
          rows={props.rows ?? 3}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          readOnly={props.readOnly}
          aria-readonly={props.readOnly || undefined}
        />
      </span>
      <span className={k.fieldHelp}>{props.help ?? ' '}</span>
    </label>
  );
}

/* ------------------------------- setup tabs -------------------------------- */

export function TabBar(props: {
  /** `count` renders a small server-supplied count chip (e.g. the unpaid queue size). */
  tabs: readonly { id: string; label: string; count?: string }[];
  active: string;
  onPick: (id: string) => void;
}) {
  return (
    <div className={f.tabs} role="tablist">
      {props.tabs.map((t) => {
        const on = t.id === props.active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            className={[f.tab, on ? f.tabOn : ''].join(' ')}
            onClick={() => props.onPick(t.id)}
          >
            {t.label}
            {t.count !== undefined && <span className={f.tabCount}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------- confirm dialog -------------------------------- */

/**
 * Consequence-first confirm (frozen rule): the dialog states the effect on
 * other people's work before the verb; error fill only on the confirming
 * button of destructive grades.
 */
export function ConfirmDialog(props: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  effectTitle?: string;
  effect?: React.ReactNode;
  confirmLabel: string;
  /** Override the default Cancel word (e.g. the بله/خیر start-session pair). */
  cancelLabel?: string;
  /** Keep the confirming button inert until a required input is present. */
  confirmDisabled?: boolean;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const intl = useIntl();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (props.open && !d.open) d.showModal();
    if (!props.open && d.open) d.close();
  }, [props.open]);

  return (
    <dialog ref={ref} className={f.dialog} onClose={props.onClose} aria-label={props.title}>
      <h2 className={f.dialogTitle}>{props.title}</h2>
      <div className={f.dialogBody}>{props.body}</div>
      {props.effect && (
        <div className={f.effectBox}>
          {props.effectTitle && <div className={f.effectTitle}>{props.effectTitle}</div>}
          {props.effect}
        </div>
      )}
      <div className={f.dialogActions}>
        <Button variant="quiet" type="button" onClick={props.onClose}>
          {props.cancelLabel ?? intl.formatMessage({ id: 'common.cancel' })}
        </Button>
        <Button
          variant={props.danger ? 'danger' : 'brand'}
          type="button"
          busy={props.busy}
          disabled={props.busy || props.confirmDisabled}
          onClick={props.onConfirm}
        >
          {props.confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}
